import {Box, Flex, Stack, Text, TextInput, Textarea} from '@mantine/core';
import * as React from 'react';

import notifications, {Response} from 'notifications';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import Modal from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';
import {ValidationRuleParams, ValidationRulesErrorResponse} from 'validation_rules/network/types';
import {validateUserInputFormula} from 'validation_rules/network/ValidationRulesDAO';

import {ConditionEvaluatorVersion} from '../constants';

interface ValidationTextAreaProps {
  label: string;
  value: string;
  onChange: (newValue: string) => void;
  required?: boolean;
  maxCharacters?: number | null;
}

function ValidationTextArea({
  label,
  value,
  onChange,
  required = false,
  maxCharacters = null,
}: ValidationTextAreaProps): JSX.Element {
  return (
    <Box>
      <Textarea
        label={label}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        rows={2}
        required={required}
        maxLength={maxCharacters ?? undefined}
      />
      {maxCharacters && (
        <Text size="sm" ta="right" mt={4}>
          {value.length}/{maxCharacters} characters
        </Text>
      )}
    </Box>
  );
}

interface ValidationRulesModalProps {
  hasValidationRulesItemId: string;
  onRequestClose: () => void;
  onRequestSubmit: (upsertParams: ValidationRuleParams) => Promise<void>;
  title?: string;
  name?: string;
  userInputFormula?: string;
  description?: string;
  errorMessage?: string | null;
  overridable?: boolean | null;
  negateResult?: boolean | null;
  errors?: Array<string> | null;
}

export default function ValidationRulesModal(props: ValidationRulesModalProps): JSX.Element {
  const [name, setName] = React.useState(props.name ?? '');
  const [description, setDescription] = React.useState(props.description ?? '');
  const [userInputFormula, setUserInputFormula] = React.useState(props.userInputFormula ?? '');
  const [errorMessage, setErrorMessage] = React.useState(props.errorMessage ?? '');
  const [validationResult, setValidationResult] = React.useState<{
    checked: boolean;
    errors: Array<string>;
  }>({
    checked: false,
    errors: props.errors ?? [],
  });

  const negateResult = props.negateResult ?? false;

  async function handleOnRequestSubmit(): Promise<void> {
    await props
      .onRequestSubmit({
        name,
        description,
        userInputFormula,
        errorMessage,
        overridable: false,
        evaluatorVersion: ConditionEvaluatorVersion.V1,
        negateResult,
      })
      .catch((payload: ValidationRulesErrorResponse) => {
        if (payload.validationRuleErrors) {
          setValidationResult({checked: true, errors: payload.validationRuleErrors});
        } else {
          notifications.error(GENERIC_ERROR_MESSAGE);
        }
      });
  }

  function handleFormulaChange(newFormula: string): void {
    setUserInputFormula(newFormula);
    if (validationResult.checked) {
      setValidationResult({checked: false, errors: []});
    }
  }

  async function handleValidateSyntax(): Promise<void> {
    await validateUserInputFormula(props.hasValidationRulesItemId, userInputFormula)
      .then(() => {
        setValidationResult({checked: true, errors: []});
      })
      .catch((resp: Response) => {
        if (resp.responseJSON) {
          const payload: ValidationRulesErrorResponse = resp.responseJSON as ValidationRulesErrorResponse;
          if (payload.validationRuleErrors) {
            setValidationResult({checked: true, errors: payload.validationRuleErrors});
          } else {
            notifications.error(GENERIC_ERROR_MESSAGE);
          }
        } else {
          notifications.notifyError(resp);
        }
      });
  }

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleOnRequestSubmit}
      title={props.title ?? 'Create rule'}
      medium={true}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
        },
        {
          text: 'Save',
          submit: true,
          style: ButtonStyle.SECONDARY,
        },
      ]}
    >
      <Stack spacing="md">
        <TextInput
          label="Name"
          value={name}
          onChange={(e: InputChangeEvent) => {
            setName(e.target.value);
          }}
          required={true}
          data-component="ValidationRulesModal"
          data-element="inputText"
          data-key="name"
          styles={(theme) => ({
            input: {
              '&:invalid': {
                // Override Benchling styles when the input is required and empty
                backgroundColor: theme.white,
                borderColor: theme.colors.gray[3],
                color: theme.colors.gray[9],
              },
            },
          })}
        />
        <ValidationTextArea label="Description" value={description} onChange={setDescription} />
        <Textarea
          label="Rule definition"
          description="Rules are defined with a formula that evaluates to TRUE or FALSE. If FALSE, users will be shown the error message below."
          value={userInputFormula}
          onChange={(e) => {
            handleFormulaChange(e.target.value);
          }}
          required={true}
          rows={3}
        />
        {validationResult.checked ? (
          <InlineBannerStack
            messages={
              validationResult.errors.length > 0
                ? validationResult.errors.map((text: string) => ({
                    bannerType: InlineBannerType.ERROR,
                    text,
                  }))
                : [
                    {
                      bannerType: InlineBannerType.SUCCESS,
                      text: 'Syntax is valid',
                    },
                  ]
            }
          />
        ) : undefined}
        <Flex justify="flex-end">
          <Button
            text="Check syntax"
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            size={ButtonSize.SMALL}
            onClick={handleValidateSyntax}
          />
        </Flex>
        <ValidationTextArea
          label="Error message"
          value={errorMessage}
          onChange={setErrorMessage}
          required={true}
          maxCharacters={250}
        />
      </Stack>
    </Modal>
  );
}
